import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Calendar } from 'lucide-react';
import { posApi } from '../../services/api';
import toast from 'react-hot-toast';
import BankAccountSelect from '../common/BankAccountSelect';
import { getCurrencySymbol } from '@/utils/currency';
import { translatePaymentMethod } from '@/utils/translateStatus';

interface AddSupplierPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  supplier: any;
}

export default function AddSupplierPaymentModal({ isOpen, onClose, supplier }: AddSupplierPaymentModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [bankAccount, setBankAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [referenceNo, setReferenceNo] = useState('');

  const maxAmount = Math.max(0, Number((supplier?.outstanding_amount || 0).toFixed(2)));

  useEffect(() => {
    if (isOpen) {
      if (maxAmount > 0) {
        setAmount(String(maxAmount));
      } else {
        setAmount('');
      }
    } else {
      setAmount('');
      setReferenceNo('');
      setPaymentMethod('Cash');
      setBankAccount('');
    }
  }, [isOpen, maxAmount]);

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!supplier?.name) throw new Error('Supplier is missing');
      const numAmount = Number(amount);
      if (isNaN(numAmount) || numAmount <= 0) {
        throw new Error(t('supplierPayment.validAmount'));
      }
      if (paymentMethod === 'Bank' && !bankAccount) {
        throw new Error(t('supplierPayment.selectBank'));
      }
      const bank = banks.find((b: { name: string; bank_name: string }) => b.name === bankAccount);

      const payload = {
        supplier: supplier.name,
        date,
        payment_method: paymentMethod,
        bank_account: paymentMethod === 'Bank' ? bankAccount : '',
        bank_name: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        account: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        amount: numAmount,
        reference_no: referenceNo,
      };

      await posApi.addSupplierPayment(payload);
    },
    onSuccess: () => {
      toast.success(t('supplierPayment.paymentAdded'));
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setAmount('');
      setReferenceNo('');
      setPaymentMethod('Cash');
      setBankAccount('');
      onClose();
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || t('supplierPayment.paymentFailed'));
    }
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-900">{t('supplierPayment.title')}</h3>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
          <p className="text-sm text-gray-600">
            {t('supplierPayment.addingFor', { name: supplier?.supplier_name })}
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.date')} *</label>
            <div className="relative">
              <Calendar className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.paymentMethod')} *</label>
            <select
              value={paymentMethod}
              onChange={e => {
                const next = e.target.value;
                setPaymentMethod(next);
                if (next === 'Bank' && !bankAccount && banks.length) {
                  const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
                  setBankAccount(def.name);
                }
                if (next !== 'Bank') setBankAccount('');
              }}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="Cash">{translatePaymentMethod('Cash', t)}</option>
              <option value="Bank">{translatePaymentMethod('Bank', t)}</option>
            </select>
          </div>

          {paymentMethod === 'Bank' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('paymentMethod.bank')} *</label>
              <BankAccountSelect
                value={bankAccount}
                onChange={setBankAccount}
                banks={banks}
                required
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.amount')} ({getCurrencySymbol()}) *</label>
            <input
              type="number"
              min="0"
              step="any"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('supplierPayment.amountPlaceholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('supplierPayment.referenceOptional')}</label>
            <input
              type="text"
              value={referenceNo}
              onChange={e => setReferenceNo(e.target.value)}
              className="w-full px-3 py-2.5 sm:py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('supplierPayment.referencePlaceholder')}
            />
          </div>
        </div>

        <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50 flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 shrink-0">
          <button type="button" onClick={onClose} className="btn-secondary w-full sm:flex-1">
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="btn-primary w-full sm:flex-1"
          >
            {saveMutation.isPending ? t('common.saving') : t('supplierPayment.savePayment')}
          </button>
        </div>
      </div>
    </div>
  );
}
